import React from 'react';
import Bluebird from 'bluebird';
import {
  Paper,
  Typography,
  FormControl,
  FormLabel,
  FormGroup,
  Checkbox,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  Grid,
  Tooltip,
} from '@mui/material';
import { InfoOutlined, Help as HelpIcon } from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import * as usersNotificationsPreferencesApi from '../api/usersNotificationsPreferences';
import snackbarHelper from '../helpers/snackbarHelper';
import * as errorsHelper from '../helpers/errors';
import * as userHelper from '../helpers/user';
import OverlayLoader from './OverlayLoader';
import { Colors } from '../assets/theme/Colors';

const NOTIFICATION_TYPE_INTERNAL = 'internal';
const NOTIFICATION_TYPE_SMS = 'sms';
const NOTIFICATION_TYPE_EMAIL = 'email';
const NOTIFICATION_TYPE_PUSH = 'push';

// RES-4735: the Site Director SMS preference is exempt from SMS-toggle hiding.
const SITE_DIRECTOR_SMS_TYPE = 'SITE_DIRECTOR_SMS';

const checkboxesAndRadiosSx = {
  paddingTop: '2.5px',
  paddingBottom: '2.5px',
};

class NotificationPreferences extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      notificationPreferences: props.notification.user_preferences,
    };
  }

  handleChange = event => {
    const { notification, user, smsTogglesHidden = false } = this.props;
    const { notificationPreferences } = this.state;
    const eventTargetName = event.target.value;
    // RES-4735: when the SMS toggle is hidden for this card, never silently re-enable SMS.
    const isSmsHiddenForCard = smsTogglesHidden && notification.type !== SITE_DIRECTOR_SMS_TYPE;

    notificationPreferences[eventTargetName] = event.target.checked;

    if (eventTargetName === 'realtime') {
      notificationPreferences.digest = false;
    } else if (eventTargetName === 'digest') {
      notificationPreferences.realtime = false;
    }
    const isUserPhoneVerified = userHelper.isUserPhoneNumberVerified(user);
    const isUserEmailVerified = userHelper.isUserEmailVerified(user);

    if (
      !notificationPreferences.internal &&
      (!notificationPreferences.email || !isUserEmailVerified) &&
      (!notificationPreferences.sms || !isUserPhoneVerified) &&
      !notificationPreferences.push
    ) {
      if (eventTargetName === 'receive') {
        notificationPreferences.internal = true;
        notificationPreferences.email = isUserEmailVerified;
        notificationPreferences.sms = isSmsHiddenForCard ? false : isUserPhoneVerified;
        // The push checkbox is only shown on native — never silently enable push on web.
        notificationPreferences.push = Capacitor.isNativePlatform();
      } else {
        notificationPreferences.receive = false;
      }
    }

    this.setState({
      loading: true,
      notificationPreferences,
    });

    return Bluebird.try(() =>
      usersNotificationsPreferencesApi.storeOrUpdate(user.id, {
        ...notificationPreferences,
        type: notification.type,
      })
    )
      .then(() => snackbarHelper.success('Notification preferences has been saved successfully!'))
      .catch(err => errorsHelper.handleError(err))
      .finally(() =>
        this.setState({
          loading: false,
        })
      );
  };

  isCheckboxDisabled = notificationType => {
    const { notificationPreferences } = this.state;
    const { notification, user, pushNotificationEnabled = false } = this.props;
    const isUserPhoneNumberVerified = userHelper.isUserPhoneNumberVerified(user);
    const isUserEmailVerified = userHelper.isUserEmailVerified(user);

    if (notificationType === NOTIFICATION_TYPE_SMS && !isUserPhoneNumberVerified) {
      return true;
    }

    if (notificationType === NOTIFICATION_TYPE_EMAIL && !isUserEmailVerified) {
      return true;
    }

    if (notificationType === NOTIFICATION_TYPE_PUSH && !pushNotificationEnabled) {
      return true;
    }

    if (!notification.mandatory) {
      return !notificationPreferences.receive;
    }

    return (
      [
        notificationPreferences[NOTIFICATION_TYPE_INTERNAL],
        notificationPreferences[NOTIFICATION_TYPE_EMAIL],
        notificationPreferences[NOTIFICATION_TYPE_SMS],
        notificationPreferences[NOTIFICATION_TYPE_PUSH],
      ].filter(isChecked => isChecked).length === 1 && notificationPreferences[notificationType]
    );
  };

  render() {
    const { loading, notificationPreferences } = this.state;
    const {
      user,
      notification,
      pushNotificationEnabled = false,
      hasPushNotificationsFF = false,
      smsTogglesHidden = false,
    } = this.props;
    const showSmsToggle =
      notification.user_may_choose_sms && (!smsTogglesHidden || notification.type === SITE_DIRECTOR_SMS_TYPE);
    const showPushToggle =
      notification.user_may_choose_push && hasPushNotificationsFF && Capacitor.isNativePlatform();
    const isDisabled = notification.immediate || !notificationPreferences.receive;
    const isUserPhoneNumberVerified = userHelper.isUserPhoneNumberVerified(user);
    const isUserEmailVerified = userHelper.isUserEmailVerified(user);

    return (
      <OverlayLoader isLoading={loading}>
        <Paper
          sx={{
            padding: { xs: '16px', sm: '16px 24px' },
          }}
        >
          <Grid container spacing={3} justifyContent="space-between" alignItems="center">
            <Grid item lg={3} md={12} xs={12}>
              <Typography variant="h6" color="textPrimary">
                {notification.display_name}
              </Typography>

              {notification.mandatory && (
                <Typography color="secondary" sx={{ marginBottom: notification.description ? '9px' : 0 }}>
                  <InfoOutlined fontSize="small" sx={{ position: 'relative', top: 5, marginRight: '2.5px' }} />
                  This notification is mandatory.
                </Typography>
              )}

              {notification.description && <Typography color="textSecondary">{notification.description}</Typography>}
            </Grid>

            <Grid item lg={3} md={4} xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    color="secondary"
                    checked={notificationPreferences.receive}
                    value="receive"
                    onChange={this.handleChange}
                    disabled={notification.immediate}
                  />
                }
                label="Yes, I want to receive this notification"
              />
            </Grid>

            <Grid item lg={3} md={4} xs={12}>
              <FormControl variant="standard" component="fieldset">
                <FormLabel component="legend">
                  <Typography variant="body2" color="textSecondary">
                    How do you want to receive this notification?
                  </Typography>
                </FormLabel>

                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={notificationPreferences.internal}
                        value={NOTIFICATION_TYPE_INTERNAL}
                        onChange={this.handleChange}
                        sx={checkboxesAndRadiosSx}
                        disabled={this.isCheckboxDisabled(NOTIFICATION_TYPE_INTERNAL)}
                        color="secondary"
                      />
                    }
                    label="by a in-app message"
                  />

                  {notification.user_may_choose_email && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isUserEmailVerified ? notificationPreferences.email : false}
                          value={NOTIFICATION_TYPE_EMAIL}
                          onChange={this.handleChange}
                          sx={checkboxesAndRadiosSx}
                          disabled={this.isCheckboxDisabled(NOTIFICATION_TYPE_EMAIL)}
                          color="secondary"
                        />
                      }
                      label={
                        <>
                          by an e-mail
                          {!isUserEmailVerified && (
                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                `Your email address is not verified. ` +
                                `Therefore, you are unable to receive notifications in this way. ` +
                                `Please verify your email in the profile section of the software.`
                              }
                              style={{ position: 'absolute', marginLeft: 5 }}
                            >
                              <HelpIcon htmlColor={Colors.blue.main} />
                            </Tooltip>
                          )}
                        </>
                      }
                    />
                  )}

                  {showSmsToggle && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isUserPhoneNumberVerified ? notificationPreferences.sms : false}
                          value={NOTIFICATION_TYPE_SMS}
                          onChange={this.handleChange}
                          sx={checkboxesAndRadiosSx}
                          disabled={this.isCheckboxDisabled(NOTIFICATION_TYPE_SMS)}
                          color="secondary"
                        />
                      }
                      label={
                        <>
                          by a text message
                          {!isUserPhoneNumberVerified && (
                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                `Your phone# is not verified, therefore you are not allowed to ` +
                                `receive notification in this way.`
                              }
                              style={{ position: 'absolute', marginLeft: 5 }}
                            >
                              <HelpIcon htmlColor={Colors.blue.main} />
                            </Tooltip>
                          )}
                        </>
                      }
                    />
                  )}

                  {showPushToggle && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={pushNotificationEnabled ? notificationPreferences.push : false}
                          value={NOTIFICATION_TYPE_PUSH}
                          onChange={this.handleChange}
                          sx={checkboxesAndRadiosSx}
                          disabled={this.isCheckboxDisabled(NOTIFICATION_TYPE_PUSH)}
                          color="secondary"
                        />
                      }
                      label={
                        <>
                          by a push notification
                          {!pushNotificationEnabled && (
                            <Tooltip
                              arrow
                              placement="top"
                              title="Please enable push notifications for Food Rescue US in your device settings to receive notification this way."
                              style={{ position: 'absolute', marginLeft: 5 }}
                            >
                              <HelpIcon htmlColor={Colors.blue.main} />
                            </Tooltip>
                          )}
                        </>
                      }
                    />
                  )}
                </FormGroup>
              </FormControl>
            </Grid>

            <Grid item lg={3} md={4} xs={12}>
              {notification.user_may_choose_digest && (
                <FormControl variant="standard" component="fieldset">
                  <Typography variant="body2" color="textSecondary">
                    When do you want to receive this notification?
                  </Typography>

                  <RadioGroup
                    aria-label="Gender"
                    name="gender1"
                    value={notificationPreferences.realtime ? 'realtime' : 'digest'}
                    onChange={this.handleChange}
                    style={{ display: 'inline' }}
                  >
                    <FormControlLabel
                      value="realtime"
                      control={<Radio color="secondary" sx={checkboxesAndRadiosSx} />}
                      label="immediately"
                      disabled={isDisabled}
                    />

                    <FormControlLabel
                      value="digest"
                      control={<Radio color="secondary" sx={checkboxesAndRadiosSx} />}
                      label="in a daily digest"
                      disabled={isDisabled}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            </Grid>
          </Grid>
        </Paper>
      </OverlayLoader>
    );
  }
}

export default NotificationPreferences;
